import React, { useMemo } from "react";
import { Box, Typography, Button, Chip, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket } from "@fortawesome/free-solid-svg-icons";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";

import crown from "../../../assets/images/SVG/crown.svg";
import Calendar from "../../../assets/images/icons/header_calendar.svg";
import * as routes from "../../../constants/routes";
import usePlanStatus from "../../../hooks/usePlanStatus";
import afinandinhoAlert from "../../../assets/images/SVG/afinandinhoAlert.svg"

const CardContainer = styled(Box)(({ theme, variant }) => ({
  backgroundColor: "#ffffff",
  borderRadius: "6px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  height: "100%",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  border: "3px solid transparent",

  "&:hover": {
    backgroundColor: "#ede3f5",
    border: `3px solid ${theme.palette.primary.main}`,
    // padding: "17px",
    "& .default-text": {
      display: "none",
    },
    "& .hover-text": {
      display: "block !important",
    },
  },
  ...(variant === "free" && {
    border: "2px solid #f15a24",
  }),
}));

const CardHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const ActiveBadge = styled(Chip)({
  backgroundColor: "#e3f8c4",
  color: "#1b6f14",
  fontWeight: 700,
  fontSize: "8px",
  textTransform: "uppercase",
  paddingInline: "2px"
});

const PlanInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const TrialIconContainer = styled(Box)({
  position: "relative",
  display: "inline-block",
  "& img": {
    width: "100px",
    height: "auto",
  },
  "& span": {
    position: "absolute",
    top: "50%",
    left: "55%",
    transform: "translate(-50%, -50%)",
    fontSize: "1.8em",
    fontWeight: 700,
    color: "#7730ac",
  },
});

const PlanDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const PlanName = styled(Typography)(({ theme }) => ({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 600,
  letterSpacing: "0.17px",
  color: "#333333",
}));

const PlanDescription = styled(Typography)({
  color: "#4d4d4d",
  fontSize: "14px",
});

const ExpirationLabel = styled(Typography)({
  color: "#4d4d4d",
  fontSize: "15px",
});

const ExpirationValue = styled(Typography)({
  color: "#4d4d4d",
  fontSize: "15px",
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  padding: "4px 24px",
  fontWeight: 700,
  fontSize: "14px",
  textTransform: "none",
  width: "fit-content"
}));

const PlanStatusCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { status, planName, expirationDate, remainingDays, paymentMethod } = usePlanStatus();
  const { session } = useSelector((state) => state.session);

  const handleSubscribe = () => {
    navigate(routes.R_BUY);
  };

  const handleMoreInfo = () => {
    navigate(routes.R_MANAGE_PLAN);
  };

  const renderTrialIcon = () => (
    <TrialIconContainer>
      <img src={Calendar} alt="Trial" />
      <span>{remainingDays}</span>
    </TrialIconContainer>
  );

  const renderWithPlan = () => (
    <CardContainer variant={status} onClick={handleMoreInfo}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box
          component="img"
          src={crown}
          width="60px"
        />
        <IconButton size="small" sx={{ color: "#333333", padding: "4px" }}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <PlanName
            sx={{
              fontSize: { xs: "12px", sm: "16px" },
              lineHeight: { xs: "13px", sm: "16px" },
              paddingRight: "10px"
            }}
          >
            {planName}

          </PlanName>
          <ActiveBadge label={t("global.my_active_plan")} size="small" />
        </Box>
        {expirationDate && (
          <ExpirationLabel className="default-text"
            sx={{
              mt: "4px",
              fontSize: { xs: "11px", sm: "15px" }
            }}
          >
            {paymentMethod === "creditcard"
              ? t("global.renews_on")
              : t("global.expiration")
            }
            <ExpirationValue component="span" sx={{ fontSize: { xs: "11px", sm: "15px" } }}>{" "} {expirationDate}</ExpirationValue>
          </ExpirationLabel>
        )}
        <Typography
          className="hover-text"
          sx={{
            display: "none",
            mt: "4px",
            color: "#662D91",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "uppercase",
            textDecoration: "underline",
          }}
        >
          {t("global.see_plan_details")}
        </Typography>
      </Box>
    </CardContainer>
  );

  const renderWithoutPlan = () => (
    <CardContainer variant={status}>
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "8px", sm: "16px" },
        padding: { xs: "8px", sm: "16px" },
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}>
        <Box sx={{
          width: "30%",
        }}>
          <img
            src={afinandinhoAlert}
            alt="Alerta"
          />
        </Box>

        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: "100%",
        }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#f15a24"
            textTransform="uppercase"
          >
            {t("global.oops_your_plan_expired")}
          </Typography>

          {expirationDate && (
            <Typography variant="body2" color="#4d4d4d">
              {t("global.your_afinando_plan_expired")} {expirationDate}
            </Typography>
          )}

          <Typography variant="body2" color="#4d4d4d">
            {t("global.return_to_unlimited_access")}
          </Typography>

          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleSubscribe}
            endIcon={<FontAwesomeIcon icon={faRocket} />}
          >
            {t("global.subscribe_to_a_plan")}
          </ActionButton>

        </Box>
      </Box>
    </CardContainer >
  );

  const renderTrial = () => (
    <CardContainer variant={status}>
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "8px", sm: "16px" },
        padding: { xs: "8px", sm: "16px" },
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}>
        <Box sx={{
          width: "30%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {renderTrialIcon()}
        </Box>

        <Box sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "20px",
          width: "100%",
          height: "100%",
          padding: { xs: "8px", sm: "0px" }
        }}>
          <CardHeader>
            <Typography variant="subtitle1" fontWeight={700} color="primary" textTransform="uppercase">
              {t("global.test_days_remaining")}
            </Typography>
          </CardHeader>

          <PlanInfo>
            <PlanDetails>
              <PlanDescription>
                {t("global.try_the_platform_for_free_for_more")} {" "}
                {remainingDays} {remainingDays > 1 ? t("global.days") : t("global.day_")}
              </PlanDescription>
            </PlanDetails>
          </PlanInfo>

          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleSubscribe}
            endIcon={<FontAwesomeIcon icon={faRocket} />}
          >
            {t("global.subscribe_to_a_plan")}
          </ActionButton>

        </Box>
      </Box>
    </CardContainer>
  );

  const layoutMap = {
    "paid": renderWithPlan,
    "free": renderWithoutPlan,
    "trial": renderTrial,
  }

  const render = useMemo(() => {
    if (!session || !status || !layoutMap.hasOwnProperty(status)) return null
    return layoutMap[status]()
  }, [status, session])

  return render
};

export default PlanStatusCard;
